import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Alert,
    Box,
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Paper,
    Stack,
    Typography,
} from "@mui/material";

import {
    ExpandLess,
    ExpandMore,
    FileDownloadOutlined as DownloadIcon,
    Groups as GroupsIcon,
    Person as PersonIcon,
    Save as SaveIcon,
    WarningAmberRounded as WarningIcon,
} from "@mui/icons-material";

import useFilteredStudents from "../hooks/useFilteredStudents";
import useSubjectsAndUnits from "../hooks/useSubjectsAndUnits";

import useBatchAssessmentData from "../hooks/batch_hooks/useBatchAssessmentData";
import useBatchAssessmentState from "../hooks/batch_hooks/useBatchAssessmentState";
import useBatchAssessmentSave from "../hooks/batch_hooks/useBatchAssessmentSave";
import useBatchEntryState from "../hooks/batch_hooks/useBatchEntryState";

import BatchAssessmentFilters from "./batchAssessment/BatchAssessmentFilters";
import BatchStudentList from "./batchAssessment/BatchStudentList";
import BatchStudentDetail from "./batchAssessment/BatchStudentDetail";
import BatchEntryPanel from "./batchAssessment/BatchEntryPanel";

import { NEUTRAL_STATUS_COLORS } from "./batchAssessment/batchAssessmentColors";

import { buildStudentReportData } from "./batchAssessment/studentReportData";
import { downloadStudentReportWord } from "./batchAssessment/studentReportPdf";

import {
    FloatingGradelyLoader,
} from "../loadingIcons/LoadingIcons";


// Batch Entry is temporarily disabled — flip back to true to restore it.
const BATCH_ENTRY_ENABLED = false;

// forwardRef lets the parent tab shell (ManageStudentProgress)
// check for unsaved edits before switching away from this tab —
// otherwise switching tabs unmounts this component and silently
// discards any unsaved work with no warning at all.
const BatchUnitProgressCardGenerator = forwardRef((props, ref) => {

    // =========================================================
    // 1. Assessment Setup / Filters
    // =========================================================

    const [
        selectedSubject,
        setSelectedSubject,
    ] = useState("");

    const [
        selectedUnit,
        setSelectedUnit,
    ] = useState("");

    const [
        selectedTerm,
        setSelectedTerm,
    ] = useState("");

    const [
        classLevel,
        setClassLevel,
    ] = useState("");

    const [
        sectionLevel,
        setSectionLevel,
    ] = useState("");

    const [
        academicYear,
        setAcademicYear,
    ] = useState("");

    const [
        searchTerm,
        setSearchTerm,
    ] = useState("");

    const [
        bulkSelectedStudentIds,
        setBulkSelectedStudentIds,
    ] = useState([]);

    const [
        feedback,
        setFeedback,
    ] = useState(null);

    // One mode is always active.
    const [
        activeAssessmentMode,
        setActiveAssessmentMode,
    ] = useState("individual");


    // =========================================================
    // 2. Existing Gradely Hooks
    // =========================================================

    const filteredStudents = useFilteredStudents(
        classLevel,
        sectionLevel,
        academicYear
    );

    const {
        subjects,
        units,
    } = useSubjectsAndUnits(
        selectedSubject
    );


    // =========================================================
    // 3. Stable Student ID List
    // =========================================================

    const studentIdsKey = useMemo(
        () =>
            (filteredStudents || [])
                .map((student) => student.id)
                .join(","),
        [filteredStudents]
    );

    const studentIds = useMemo(() => {
        if (!studentIdsKey) {
            return [];
        }

        return studentIdsKey
            .split(",")
            .map(Number);
    }, [studentIdsKey]);

    // Identifies the active Subject/Unit/Term/student-filter
    // request. useBatchAssessmentState uses this to tell a
    // genuine filter change (safe to rebuild every student from
    // the server) apart from a same-context refetch after a
    // save (must not discard another student's unsaved edits) —
    // a Term or Unit change can return the exact same roster of
    // student IDs, so the roster alone isn't a reliable signal.
    const assessmentContextKey = useMemo(
        () =>
            [
                selectedSubject,
                selectedUnit,
                selectedTerm,
                studentIdsKey,
            ].join("|"),
        [
            selectedSubject,
            selectedUnit,
            selectedTerm,
            studentIdsKey,
        ]
    );


    // =========================================================
    // 4. Retrieve Batch Assessment Data
    // =========================================================

    const {
        data: batchData,

        skills,
        dokLevels,
        students: batchStudents,

        totalSkills,

        isLoading,
        error: retrieveError,

        refetch,
    } = useBatchAssessmentData({
        subjectId: selectedSubject,
        unitId: selectedUnit,
        termNumber: selectedTerm,
        studentIds,
    });


    // =========================================================
    // 5. Editable Assessment State
    // =========================================================

    const {
        studentState,

        selectedStudentId,

        selectedStudent:
            selectedStudentState,

        dirtyStudentIds,

        setSelectedStudentId,

        setSkillAchievement,
        setDOKProficiency,
        setOverallAssessment,
        setEducatorComment,

        applyBatchChanges,
        resetToServerState,
        markStudentsClean,
    } = useBatchAssessmentState({
        students: batchStudents,
        skills,
        dokLevels,
        contextKey: assessmentContextKey,
    });


    // =========================================================
    // 6. Batch Entry Draft State
    // =========================================================

    const {
        selectedSkillIds:
            batchSelectedSkillIds,

        dokProficiency:
            batchDOKProficiency,

        overallAssessment:
            batchOverallAssessment,

        toggleSkill:
            toggleBatchSkill,

        setDOKProficiency:
            setBatchDOKProficiency,

        setOverallAssessment:
            setBatchOverallAssessment,

        clearBatchEntry,

        hasBatchChanges,
    } = useBatchEntryState();


    // =========================================================
    // 7. Save Hook
    // =========================================================

    // Set immediately before each saveStudents(...) call so
    // handleSaveSuccess below knows exactly which students were
    // just saved — saveStudents calls onSaveSuccess (this) and
    // awaits it BEFORE returning, so this is the only place the
    // dirty flag can be cleared ahead of the refetch it triggers.
    const pendingSaveIdsRef = useRef([]);

    const handleSaveSuccess = useCallback(
        async () => {
            // Clear the dirty flag for exactly the students
            // just saved BEFORE refetching. The refetch below
            // changes `batchStudents`, which re-runs the state
            // hook's same-roster merge — that merge preserves
            // any student still marked dirty, so saved students
            // must already be clean or they'd wrongly be treated
            // as having unsaved edits forever.
            markStudentsClean(
                pendingSaveIdsRef.current
            );

            await refetch();
        },
        [refetch, markStudentsClean]
    );

    const {
        saveStudents,
        isSaving,
        saveError,
        clearSaveError,
    } = useBatchAssessmentSave({
        subjectId: selectedSubject,
        unitId: selectedUnit,
        termNumber: selectedTerm,

        skills,
        dokLevels,
        students: batchStudents,
        studentState,

        onSaveSuccess:
            handleSaveSuccess,
    });


    // =========================================================
    // 7b. Unsaved-Changes Guard
    //
    // A student can have unsaved edits without being checked
    // for Save Selected (they were edited, then the checkbox
    // was cleared, or another student was opened instead). Any
    // such student is at risk two ways:
    //   - Switching Subject/Unit/Term or the assessment mode
    //     tab discards their edits outright (genuinely different
    //     context — see resetToServerState / the contextKey
    //     check in useBatchAssessmentState).
    //   - Clicking Save Selected only saves the checked set —
    //     their edits are NOT discarded (useBatchAssessmentState
    //     preserves them), but the teacher may believe everyone
    //     was just saved when they weren't.
    // Both cases interrupt with a dialog naming those students
    // rather than proceeding silently.
    // =========================================================

    const dirtyStudents = useMemo(
        () =>
            dirtyStudentIds
                .map((studentId) =>
                    batchStudents.find(
                        (student) =>
                            student.student ===
                            studentId
                    )
                )
                .filter(Boolean),
        [
            dirtyStudentIds,
            batchStudents,
        ]
    );

    // Dirty students NOT currently checked for Save Selected —
    // the set actually at risk of either case above.
    const unselectedDirtyStudents = useMemo(
        () =>
            dirtyStudents.filter(
                (student) =>
                    !bulkSelectedStudentIds.includes(
                        student.student
                    )
            ),
        [
            dirtyStudents,
            bulkSelectedStudentIds,
        ]
    );

    // Lets the parent tab shell (ManageStudentProgress) check
    // for unsaved edits before switching away from this tab —
    // switching tabs unmounts this component outright, which
    // would otherwise discard everything with no warning.
    useImperativeHandle(
        ref,
        () => ({
            hasUnsavedChanges: () =>
                dirtyStudentIds.length > 0,

            getUnsavedStudentNames: () =>
                dirtyStudents.map(
                    (student) =>
                        student.student_full_name
                ),
        }),
        [
            dirtyStudentIds,
            dirtyStudents,
        ]
    );

    // `mode` distinguishes which dialog copy/actions to show:
    // "navigation" (Subject/Unit/Term/mode-tab change — switching
    // away discards those edits) vs "save" (Save Selected clicked
    // — those edits are safe, just not part of this save).
    const [
        unsavedChangesDialog,
        setUnsavedChangesDialog,
    ] = useState({
        open: false,
        mode: null,
        pendingAction: null,
    });

    const guardUnsavedChanges = useCallback(
        (action) => {
            if (unselectedDirtyStudents.length > 0) {
                setUnsavedChangesDialog({
                    open: true,
                    mode: "navigation",
                    pendingAction: action,
                });

                return;
            }

            action();
        },
        [unselectedDirtyStudents]
    );

    const handleSelectUnsavedStudents =
        () => {
            setBulkSelectedStudentIds(
                (previous) => [
                    ...previous,
                    ...unselectedDirtyStudents.map(
                        (student) =>
                            student.student
                    ),
                ]
            );

            setUnsavedChangesDialog({
                open: false,
                mode: null,
                pendingAction: null,
            });
        };

    const handleProceedAnyway = () => {
        const { pendingAction } =
            unsavedChangesDialog;

        setUnsavedChangesDialog({
            open: false,
            mode: null,
            pendingAction: null,
        });

        if (pendingAction) {
            pendingAction();
        }
    };

    const handleCancelUnsavedDialog =
        () => {
            setUnsavedChangesDialog({
                open: false,
                mode: null,
                pendingAction: null,
            });
        };


    // =========================================================
    // 7c. Warn Before Closing/Refreshing the Tab
    // =========================================================

    useEffect(() => {
        if (dirtyStudentIds.length === 0) {
            return undefined;
        }

        const handleBeforeUnload = (
            event
        ) => {
            event.preventDefault();

            // Legacy property some browsers still require to
            // trigger the native "leave site?" prompt.
            event.returnValue = "";

            return "";
        };

        window.addEventListener(
            "beforeunload",
            handleBeforeUnload
        );

        return () =>
            window.removeEventListener(
                "beforeunload",
                handleBeforeUnload
            );
    }, [dirtyStudentIds]);


    // =========================================================
    // 8. Reset Dependent Fields
    // =========================================================

    const handleSubjectChange = (
        subjectId
    ) => {
        guardUnsavedChanges(() => {
            setSelectedSubject(
                subjectId
            );

            setSelectedUnit("");
            setSearchTerm("");

            setBulkSelectedStudentIds(
                []
            );

            clearBatchEntry();

            setFeedback(null);
            clearSaveError();
        });
    };


    const handleUnitChange = (
        unitId
    ) => {
        guardUnsavedChanges(() => {
            setSelectedUnit(
                unitId
            );

            setSearchTerm("");

            setBulkSelectedStudentIds(
                []
            );

            clearBatchEntry();

            setFeedback(null);
            clearSaveError();
        });
    };


    const handleTermChange = (
        term
    ) => {
        guardUnsavedChanges(() => {
            setSelectedTerm(
                term
            );

            setBulkSelectedStudentIds(
                []
            );

            clearBatchEntry();

            setFeedback(null);
            clearSaveError();
        });
    };


    // =========================================================
    // 9. Keep Bulk Selection Valid
    // =========================================================

    useEffect(() => {
        const validIds =
            new Set(studentIds);

        setBulkSelectedStudentIds(
            (previous) =>
                previous.filter(
                    (studentId) =>
                        validIds.has(
                            studentId
                        )
                )
        );
    }, [
        studentIdsKey,
        studentIds,
    ]);


    // =========================================================
    // 10. Student Search
    // =========================================================

    const visibleStudents =
        useMemo(() => {

            const query =
                searchTerm
                    .trim()
                    .toLowerCase();

            if (!query) {
                return batchStudents;
            }

            return batchStudents.filter(
                (student) =>
                    (
                        student.student_full_name ||
                        ""
                    )
                        .toLowerCase()
                        .includes(query)
            );

        }, [
            batchStudents,
            searchTerm,
        ]);


    // =========================================================
    // 11. Selected Student Record
    // =========================================================

    const selectedStudentRecord =
        useMemo(
            () =>
                batchStudents.find(
                    (student) =>
                        student.student ===
                        selectedStudentId
                ) || null,
            [
                batchStudents,
                selectedStudentId,
            ]
        );


    // =========================================================
    // 12. Assessment Mode
    // =========================================================

    const handleAssessmentModeToggle = (
        mode
    ) => {
        if (mode === activeAssessmentMode) {
            return;
        }

        // Switching tabs resets all working state, so both tabs
        // always start fresh rather than carrying over whatever
        // was left from the other tab. This includes discarding
        // any unsaved Skill/DOK/overall edits — not just which
        // student or checkboxes were selected. Guarded above so
        // that discard is never silent.

        guardUnsavedChanges(() => {
            setActiveAssessmentMode(
                mode
            );

            setSelectedStudentId(
                null
            );

            setBulkSelectedStudentIds(
                []
            );

            clearBatchEntry();

            resetToServerState();
        });
    };


    // =========================================================
    // 13. Bulk Student Selection
    // =========================================================

    const handleToggleBulkStudent = (
        studentId
    ) => {
        setBulkSelectedStudentIds(
            (previous) => {

                if (
                    previous.includes(
                        studentId
                    )
                ) {
                    return previous.filter(
                        (id) =>
                            id !== studentId
                    );
                }

                return [
                    ...previous,
                    studentId,
                ];
            }
        );
    };


    // Opening a student's detail view also marks them selected
    // for bulk actions — no separate checkbox click required.
    const handleSelectStudent = (
        studentId
    ) => {
        setSelectedStudentId(
            studentId
        );

        setBulkSelectedStudentIds(
            (previous) =>
                previous.includes(
                    studentId
                )
                    ? previous
                    : [
                          ...previous,
                          studentId,
                      ]
        );
    };


    // =========================================================
    // 14. Individual Student Handlers
    // =========================================================

    const handleSkillChange = (
        skillId,
        isAchieved
    ) => {
        if (!selectedStudentId) {
            return;
        }

        setSkillAchievement(
            selectedStudentId,
            skillId,
            isAchieved
        );
    };


    const handleProficiencyChange = (
        dokLevel,
        proficiency
    ) => {
        if (!selectedStudentId) {
            return;
        }

        setDOKProficiency(
            selectedStudentId,
            dokLevel,
            proficiency
        );
    };


    const handleOverallChange = (
        value
    ) => {
        if (!selectedStudentId) {
            return;
        }

        setOverallAssessment(
            selectedStudentId,
            value
        );
    };


    const handleCommentChange = (
        value
    ) => {
        if (!selectedStudentId) {
            return;
        }

        setEducatorComment(
            selectedStudentId,
            value
        );
    };


    // =========================================================
    // 15. Apply Batch Entry
    // =========================================================

    const handleApplyBatchEntry = () => {

        if (
            bulkSelectedStudentIds.length === 0 ||
            !hasBatchChanges
        ) {
            return;
        }

        applyBatchChanges({
            studentIds:
                bulkSelectedStudentIds,

            selectedSkillIds:
                batchSelectedSkillIds,

            dokProficiency:
                batchDOKProficiency,

            overallAssessment:
                batchOverallAssessment,
        });

        setFeedback({
            severity: "success",
            message:
                `Batch entry applied to ` +
                `${bulkSelectedStudentIds.length} ` +
                `student${
                    bulkSelectedStudentIds.length === 1
                        ? ""
                        : "s"
                }. Review the changes before saving.`,
        });

        clearBatchEntry();

        // Return to individual review after applying.
        setActiveAssessmentMode(
            "individual"
        );
    };


    // =========================================================
    // 16. Save Selected — Individual Assessment
    // =========================================================

    const performSaveSelected = async (
        studentIdsToSave
    ) => {
        setFeedback(null);

        // Capture the exact set being saved —
        // bulkSelectedStudentIds is cleared below on success,
        // and the save itself is async, so this avoids saving/
        // clearing the wrong set if selection changes mid-flight.
        // handleSaveSuccess (called inside saveStudents, before
        // the refetch it triggers) reads this ref to know which
        // students to mark clean.
        pendingSaveIdsRef.current =
            studentIdsToSave;

        const result =
            await saveStudents(
                studentIdsToSave
            );

        if (result.success) {

            setFeedback({
                severity: "success",
                message:
                    result.data
                        ?.message ||
                    "Selected assessments saved successfully.",
            });

            setBulkSelectedStudentIds(
                []
            );
        }
    };

    // Clicking Save Selected only saves the checked students.
    // Saving no longer discards anyone else's edits, but a
    // teacher who has unsaved edits on a student they forgot to
    // check may believe this saved everyone. Interrupt and name
    // them rather than saving silently.
    const handleSaveSelected = async () => {
        if (unselectedDirtyStudents.length > 0) {
            setUnsavedChangesDialog({
                open: true,
                mode: "save",
                pendingAction: null,
            });

            return;
        }

        await performSaveSelected(
            bulkSelectedStudentIds
        );
    };

    const handleSaveSelectedOnly =
        async () => {
            setUnsavedChangesDialog({
                open: false,
                mode: null,
                pendingAction: null,
            });

            await performSaveSelected(
                bulkSelectedStudentIds
            );
        };


    // =========================================================
    // 17. Download Report — Individual Assessment
    // =========================================================

    const handleDownloadReport =
        () => {
            if (
                !selectedStudentRecord ||
                !selectedStudentState
            ) {
                return;
            }

            const reportData =
                buildStudentReportData({
                    student:
                        selectedStudentRecord,

                    studentState:
                        selectedStudentState,

                    skills,
                    dokLevels,

                    subjectName:
                        batchData?.subject
                            ?.subject_name,

                    unitName:
                        batchData?.unit
                            ?.unit_name,

                    termNumber:
                        batchData?.term_number,
                });

            downloadStudentReportWord(
                reportData
            );
        };


    // =========================================================
    // 18. Setup Status
    // =========================================================

    const assessmentSetupComplete =
        Boolean(
            selectedSubject &&
            selectedUnit &&
            selectedTerm
        );


    // =========================================================
    // Render
    // =========================================================

    return (
        <Box>

            <Stack spacing={2.5}>

                {/* =============================================
                    PAGE HEADER
                ============================================= */}

                <Box>
                    <Typography
                        variant="h5"
                        fontWeight={700}
                    >
                        Record Assessment
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mt: 0.5,
                        }}
                    >
                        Select a unit, choose students, and record the skills they have demonstrated.
                    </Typography>
                </Box>


                {/* =============================================
                    FILTERS
                ============================================= */}

                <BatchAssessmentFilters
                    subjects={subjects}
                    units={units}

                    selectedSubject={
                        selectedSubject
                    }

                    selectedUnit={
                        selectedUnit
                    }

                    selectedTerm={
                        selectedTerm
                    }

                    classLevel={
                        classLevel
                    }

                    sectionLevel={
                        sectionLevel
                    }

                    academicYear={
                        academicYear
                    }

                    searchTerm={
                        searchTerm
                    }

                    onSubjectChange={
                        handleSubjectChange
                    }

                    onUnitChange={
                        handleUnitChange
                    }

                    onTermChange={
                        handleTermChange
                    }

                    onClassLevelChange={
                        setClassLevel
                    }

                    onSectionLevelChange={
                        setSectionLevel
                    }

                    onAcademicYearChange={
                        setAcademicYear
                    }

                    onSearchChange={
                        setSearchTerm
                    }

                    resultCount={
                        assessmentSetupComplete
                            ? visibleStudents.length
                            : null
                    }
                />


                {/* =============================================
                    INITIAL SETUP
                ============================================= */}

                {!assessmentSetupComplete && (
                    <Alert severity="info">
                        Select a Subject, Unit and
                        Term to load student
                        assessments.
                    </Alert>
                )}


                {/* =============================================
                    ERRORS / FEEDBACK
                ============================================= */}

                {retrieveError && (
                    <Alert severity="error">
                        {retrieveError}
                    </Alert>
                )}

                {feedback && (
                    <Alert
                        severity={
                            feedback.severity
                        }
                        onClose={() =>
                            setFeedback(null)
                        }
                    >
                        {feedback.message}
                    </Alert>
                )}


                {/* =============================================
                    LOADING
                ============================================= */}

                {isLoading && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 5,
                            border: "1px solid",
                            borderColor:
                                "divider",
                            borderRadius: 3,
                            textAlign:
                                "center",
                        }}
                    >
                        <FloatingGradelyLoader
                            size={55}
                            showText={true}
                            text="Loading student assessments..."
                        />
                    </Paper>
                )}


                {/* =============================================
                    LOADED WORKSPACE
                ============================================= */}

                {!isLoading &&
                    batchData &&
                    batchStudents.length > 0 && (
                        <>

                            {/* =================================
                                ASSESSMENT WORKSPACE
                            ================================= */}

                            <Grid
                                container
                                spacing={2}
                                alignItems="stretch"
                            >

                                {/* =============================
                                    LEFT — STUDENTS
                                ============================= */}

                                <Grid
                                    item
                                    xs={12}
                                    md={4}
                                    lg={3.5}
                                >
                                    <BatchStudentList
                                        students={
                                            visibleStudents
                                        }

                                        studentState={
                                            studentState
                                        }

                                        selectedStudentId={
                                            selectedStudentId
                                        }

                                        onSelectStudent={
                                            handleSelectStudent
                                        }

                                        bulkSelectedStudentIds={
                                            bulkSelectedStudentIds
                                        }

                                        onToggleBulkStudent={
                                            handleToggleBulkStudent
                                        }

                                        totalSkills={
                                            totalSkills
                                        }
                                    />
                                </Grid>


                                {/* =============================
                                    RIGHT — WORKSPACE
                                ============================= */}

                                <Grid
                                    item
                                    xs={12}
                                    md={8}
                                    lg={8.5}
                                >
                                    <Stack spacing={2}>

                                        {/* =====================
                                            MODE SELECTORS

                                            While Batch Entry is
                                            disabled, Individual is
                                            the only mode — a slim
                                            plain header reads better
                                            than a full-width empty
                                            toggle card. The original
                                            two-card picker is kept
                                            intact (and used again)
                                            for when Batch Entry is
                                            re-enabled.
                                        ===================== */}

                                        {!BATCH_ENTRY_ENABLED && (
                                            <Stack
                                                direction="row"
                                                spacing={1.25}
                                                alignItems="center"
                                                sx={{ px: 0.5 }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius:
                                                            "50%",
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        justifyContent:
                                                            "center",
                                                        flexShrink: 0,
                                                        backgroundColor:
                                                            "#738B3B",
                                                    }}
                                                >
                                                    <PersonIcon
                                                        sx={{
                                                            fontSize: 18,
                                                            color:
                                                                "#FFFFFF",
                                                        }}
                                                    />
                                                </Box>

                                                <Box>
                                                    <Typography
                                                        fontWeight={700}
                                                        fontSize="1rem"
                                                    >
                                                        3. Record Skills
                                                    </Typography>

                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Check the skills each student has demonstrated and select a proficiency level.
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        )}

                                        {BATCH_ENTRY_ENABLED && (
                                        <Grid
                                            container
                                            spacing={1.5}
                                        >

                                            {/* -----------------
                                                INDIVIDUAL
                                            ----------------- */}

                                            <Grid
                                                item
                                                xs={12}
                                                sm={6}
                                            >
                                                <Paper
                                                    elevation={
                                                        activeAssessmentMode ===
                                                        "individual"
                                                            ? 3
                                                            : 0
                                                    }

                                                    onClick={() =>
                                                        handleAssessmentModeToggle(
                                                            "individual"
                                                        )
                                                    }

                                                    sx={{
                                                        p: 2,

                                                        border:
                                                            activeAssessmentMode ===
                                                            "individual"
                                                                ? "2px solid #738B3B"
                                                                : "1px solid",

                                                        borderColor:
                                                            activeAssessmentMode ===
                                                            "individual"
                                                                ? "#738B3B"
                                                                : "divider",

                                                        backgroundColor:
                                                            activeAssessmentMode ===
                                                            "individual"
                                                                ? "#EEF3E4"
                                                                : "#ffffff",

                                                        borderRadius: 3,

                                                        cursor:
                                                            "pointer",

                                                        transition:
                                                            "all 0.2s ease",

                                                        "&:hover": {
                                                            borderColor:
                                                                "#738B3B",
                                                            backgroundColor:
                                                                "#F5F7F1",
                                                        },
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        spacing={1}
                                                    >
                                                        <Stack
                                                            direction="row"
                                                            spacing={1.25}
                                                            alignItems="center"
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 36,
                                                                    height: 36,
                                                                    borderRadius:
                                                                        "50%",
                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    justifyContent:
                                                                        "center",
                                                                    flexShrink: 0,
                                                                    backgroundColor:
                                                                        activeAssessmentMode ===
                                                                        "individual"
                                                                            ? "#738B3B"
                                                                            : "#EEF0E6",
                                                                }}
                                                            >
                                                                <PersonIcon
                                                                    sx={{
                                                                        fontSize: 20,
                                                                        color:
                                                                            activeAssessmentMode ===
                                                                            "individual"
                                                                                ? "#FFFFFF"
                                                                                : "#738B3B",
                                                                    }}
                                                                />
                                                            </Box>

                                                            <Box>
                                                                <Typography
                                                                    fontWeight={700}
                                                                    fontSize="1rem"
                                                                >
                                                                    Record Student Assessment
                                                                </Typography>

                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                >
                                                                    Assess one student
                                                                    in detail
                                                                </Typography>
                                                            </Box>
                                                        </Stack>

                                                        {activeAssessmentMode ===
                                                        "individual" ? (
                                                            <ExpandLess />
                                                        ) : (
                                                            <ExpandMore />
                                                        )}
                                                    </Stack>
                                                </Paper>
                                            </Grid>


                                            {/* -----------------
                                                BATCH ENTRY
                                                (hidden while BATCH_ENTRY_ENABLED
                                                is false — code kept intact so
                                                it can be re-enabled later)
                                            ----------------- */}

                                            {BATCH_ENTRY_ENABLED && (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    sm={6}
                                                >
                                                    <Paper
                                                        elevation={
                                                            activeAssessmentMode ===
                                                            "batch"
                                                                ? 3
                                                                : 0
                                                        }

                                                        onClick={() =>
                                                            handleAssessmentModeToggle(
                                                                "batch"
                                                            )
                                                        }

                                                        sx={{
                                                            p: 2,

                                                            border:
                                                                activeAssessmentMode ===
                                                                "batch"
                                                                    ? "2px solid #738B3B"
                                                                    : "1px solid",

                                                            borderColor:
                                                                activeAssessmentMode ===
                                                                "batch"
                                                                    ? "#738B3B"
                                                                    : "divider",

                                                            backgroundColor:
                                                                activeAssessmentMode ===
                                                                "batch"
                                                                    ? "#EEF3E4"
                                                                    : "#ffffff",

                                                            borderRadius: 3,

                                                            cursor:
                                                                "pointer",

                                                            transition:
                                                                "all 0.2s ease",

                                                            "&:hover": {
                                                                borderColor:
                                                                    "#738B3B",
                                                                backgroundColor:
                                                                    "#F5F7F1",
                                                            },
                                                        }}
                                                    >
                                                        <Stack
                                                            direction="row"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                            spacing={1}
                                                        >
                                                            <Stack
                                                                direction="row"
                                                                spacing={1.25}
                                                                alignItems="center"
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 36,
                                                                        height: 36,
                                                                        borderRadius:
                                                                            "50%",
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        justifyContent:
                                                                            "center",
                                                                        flexShrink: 0,
                                                                        backgroundColor:
                                                                            activeAssessmentMode ===
                                                                            "batch"
                                                                                ? "#738B3B"
                                                                                : "#EEF0E6",
                                                                    }}
                                                                >
                                                                    <GroupsIcon
                                                                        sx={{
                                                                            fontSize: 20,
                                                                            color:
                                                                                activeAssessmentMode ===
                                                                                "batch"
                                                                                    ? "#FFFFFF"
                                                                                    : "#738B3B",
                                                                        }}
                                                                    />
                                                                </Box>

                                                                <Box>
                                                                    <Typography
                                                                        fontWeight={700}
                                                                        fontSize="1rem"
                                                                    >
                                                                        Batch Entry
                                                                    </Typography>

                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                    >
                                                                        Apply to selected
                                                                        students
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>

                                                            <Stack
                                                                direction="row"
                                                                spacing={0.75}
                                                                alignItems="center"
                                                            >
                                                                <Chip
                                                                    size="small"

                                                                    label={
                                                                        `${bulkSelectedStudentIds.length} selected`
                                                                    }

                                                                    variant={
                                                                        bulkSelectedStudentIds.length >
                                                                        0
                                                                            ? "filled"
                                                                            : "outlined"
                                                                    }

                                                                    sx={{
                                                                        fontWeight: 700,

                                                                        backgroundColor:
                                                                            bulkSelectedStudentIds.length >
                                                                            0
                                                                                ? "#A9BB7D"
                                                                                : "transparent",

                                                                        color:
                                                                            "#33401F",
                                                                    }}
                                                                />

                                                                {activeAssessmentMode ===
                                                                "batch" ? (
                                                                    <ExpandLess />
                                                                ) : (
                                                                    <ExpandMore />
                                                                )}
                                                            </Stack>
                                                        </Stack>
                                                    </Paper>
                                                </Grid>
                                            )}

                                        </Grid>
                                        )}


                                        {/* =====================
                                            BATCH ENTRY CONTENT
                                        ===================== */}

                                        <Collapse
                                            in={
                                                activeAssessmentMode ===
                                                "batch"
                                            }
                                            timeout="auto"
                                            unmountOnExit
                                        >
                                            <BatchEntryPanel
                                                selectedStudentCount={
                                                    bulkSelectedStudentIds.length
                                                }

                                                hasBatchChanges={
    hasBatchChanges
}

                                                skills={
                                                    skills
                                                }

                                                dokLevels={
                                                    dokLevels
                                                }

                                                selectedSkillIds={
                                                    batchSelectedSkillIds
                                                }

                                                dokProficiency={
                                                    batchDOKProficiency
                                                }

                                                overallAssessment={
                                                    batchOverallAssessment
                                                }

                                                onToggleSkill={
                                                    toggleBatchSkill
                                                }

                                                onDOKProficiencyChange={
                                                    setBatchDOKProficiency
                                                }

                                                onOverallAssessmentChange={
                                                    setBatchOverallAssessment
                                                }

                                                onApply={
                                                    handleApplyBatchEntry
                                                }

                                                onClear={
                                                    clearBatchEntry
                                                }
                                            />
                                        </Collapse>


                                        {/* =====================
                                            INDIVIDUAL CONTENT
                                        ===================== */}

                                        <Collapse
                                            in={
                                                activeAssessmentMode ===
                                                "individual"
                                            }
                                            timeout="auto"
                                            unmountOnExit
                                        >
                                            <Stack spacing={1.5}>

                                                {/* =================
                                                    INDIVIDUAL SAVE BAR
                                                ================= */}

                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        px: 2,
                                                        py: 1.5,

                                                        border:
                                                            "1px solid",

                                                        borderColor:
                                                            bulkSelectedStudentIds.length >
                                                            0
                                                                ? "#A9BB7D"
                                                                : "divider",

                                                        backgroundColor:
                                                            bulkSelectedStudentIds.length >
                                                            0
                                                                ? "#F7F9F2"
                                                                : "#ffffff",

                                                        borderRadius: 2.5,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            rowGap: 1.25,
                                                            columnGap: 1.5,
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                component="div"
                                                            >
                                                                {bulkSelectedStudentIds.length >
                                                                0
                                                                    ? `${bulkSelectedStudentIds.length} student${
                                                                          bulkSelectedStudentIds.length ===
                                                                          1
                                                                              ? ""
                                                                              : "s"
                                                                      } selected`
                                                                    : "Select student checkboxes to use Save Selected"}
                                                            </Typography>

                                                            {dirtyStudentIds.length >
                                                                0 && (
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={0.75}
                                                                    alignItems="center"
                                                                    sx={{
                                                                        mt: 0.5,
                                                                    }}
                                                                >
                                                                    <Chip
                                                                        size="small"
                                                                        label={`${dirtyStudentIds.length} unsaved`}
                                                                        sx={{
                                                                            fontWeight: 700,
                                                                            height: 20,
                                                                            fontSize: "0.7rem",
                                                                            color: NEUTRAL_STATUS_COLORS
                                                                                .unsaved
                                                                                .text,
                                                                            backgroundColor:
                                                                                NEUTRAL_STATUS_COLORS
                                                                                    .unsaved
                                                                                    .bg,
                                                                            border: "1px solid",
                                                                            borderColor:
                                                                                NEUTRAL_STATUS_COLORS
                                                                                    .unsaved
                                                                                    .border,
                                                                        }}
                                                                    />

                                                                    <Button
                                                                        size="small"
                                                                        onClick={
                                                                            handleSelectUnsavedStudents
                                                                        }
                                                                        sx={{
                                                                            textTransform:
                                                                                "none",
                                                                            fontWeight: 700,
                                                                            fontSize: "0.75rem",
                                                                            minWidth: 0,
                                                                            p: 0,
                                                                            color: "#586735",
                                                                            "&:hover": {
                                                                                backgroundColor:
                                                                                    "transparent",
                                                                                textDecoration:
                                                                                    "underline",
                                                                            },
                                                                        }}
                                                                    >
                                                                        Select all unsaved
                                                                    </Button>
                                                                </Stack>
                                                            )}
                                                        </Box>

                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                flexWrap: "wrap",
                                                                gap: 1,
                                                                alignItems: "center",
                                                            }}
                                                        >
                                                            <Button
                                                                variant="outlined"
                                                                startIcon={
                                                                    <DownloadIcon />
                                                                }

                                                                disabled={
                                                                    !selectedStudentRecord
                                                                }

                                                                onClick={
                                                                    handleDownloadReport
                                                                }

                                                                sx={{
                                                                    textTransform: "none",
                                                                    fontWeight: 700,

                                                                    maxWidth: {
                                                                        xs: 260,
                                                                        sm: 340,
                                                                    },

                                                                    px: 2,
                                                                    py: 0.85,

                                                                    borderRadius: 2,

                                                                    borderColor: "#C7CCB8",
                                                                    color: "#586735",

                                                                    backgroundColor: "#FFFFFF",

                                                                    "&:hover": {
                                                                        borderColor: "#9AAC70",
                                                                        backgroundColor: "#F3F6EC",
                                                                    },
                                                                }}
                                                            >
                                                                <Box
                                                                    component="span"
                                                                    sx={{
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap",
                                                                        minWidth: 0,
                                                                    }}
                                                                >
                                                                    {selectedStudentRecord
                                                                        ? `Download Student Report`
                                                                        : "Download Report"}
                                                                </Box>
                                                            </Button>

                                                            <Button
                                                                variant="contained"
                                                                startIcon={
                                                                    <SaveIcon />
                                                                }

                                                                disabled={
                                                                    isSaving ||
                                                                    bulkSelectedStudentIds.length ===
                                                                        0
                                                                }

                                                                onClick={
                                                                    handleSaveSelected
                                                                }

sx={{
    textTransform: "none",
    fontWeight: 700,
    whiteSpace: "nowrap",

    px: 2.25,
    py: 0.85,

    borderRadius: 2,

    backgroundColor: "#738B3B",

    boxShadow:
        "0 2px 6px rgba(70, 85, 35, 0.18)",

    "&:hover": {
        backgroundColor: "#647A33",
        boxShadow:
            "0 3px 8px rgba(70, 85, 35, 0.24)",
    },
}}
                                                            >
                                                                Save Selected Students
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                </Paper>


                                                {/* =================
                                                    STUDENT DETAIL
                                                ================= */}

                                                <BatchStudentDetail
                                                    student={
                                                        selectedStudentRecord
                                                    }

                                                    studentState={
                                                        selectedStudentState
                                                    }

                                                    skills={
                                                        skills
                                                    }

                                                    dokLevels={
                                                        dokLevels
                                                    }

                                                    onSkillChange={
                                                        handleSkillChange
                                                    }

                                                    onProficiencyChange={
                                                        handleProficiencyChange
                                                    }

                                                    onOverallAssessmentChange={
                                                        handleOverallChange
                                                    }

                                                    onCommentChange={
                                                        handleCommentChange
                                                    }
                                                />

                                            </Stack>
                                        </Collapse>

                                    </Stack>
                                </Grid>

                            </Grid>

                        </>
                    )}


                {/* =============================================
                    NO STUDENTS
                ============================================= */}

                {!isLoading &&
                    assessmentSetupComplete &&
                    batchData &&
                    batchStudents.length === 0 && (
                        <Alert severity="info">
                            No students were found
                            for the selected student
                            filters.
                        </Alert>
                    )}

            </Stack>


            {/* =============================================
                UNSAVED CHANGES WARNING

                Interrupts two actions whenever a student has
                unsaved edits but is not checked for Save
                Selected:
                  - "navigation": about to switch Subject/Unit/
                    Term or the assessment mode tab — that
                    discards those edits outright.
                  - "save": Save Selected was clicked — those
                    edits are safe, but won't be part of this
                    save, so the teacher could mistakenly think
                    everyone was just saved.
                Either way, the affected students are named
                rather than this happening silently.
            ============================================= */}

            <Dialog
                open={
                    unsavedChangesDialog.open
                }

                onClose={
                    handleCancelUnsavedDialog
                }

                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontWeight: 700,
                        color: "#9A6A00",
                    }}
                >
                    <WarningIcon
                        sx={{
                            color: "#EFCE86",
                        }}
                    />
                    {unsavedChangesDialog.mode ===
                    "save"
                        ? "Some Students Won't Be Saved"
                        : "Unsaved Changes"}
                </DialogTitle>

                <DialogContent>
                    <Typography
                        variant="body2"
                        sx={{ mb: 1.5 }}
                    >
                        {`${unselectedDirtyStudents.length} student${
                            unselectedDirtyStudents.length ===
                            1
                                ? ""
                                : "s"
                        } ${
                            unselectedDirtyStudents.length ===
                            1
                                ? "has"
                                : "have"
                        } unsaved changes but ${
                            unselectedDirtyStudents.length ===
                            1
                                ? "isn't"
                                : "aren't"
                        } checked for Save Selected:`}
                    </Typography>

                    <Stack
                        spacing={0.5}
                        sx={{ mb: 1.5 }}
                    >
                        {unselectedDirtyStudents.map(
                            (student) => (
                                <Typography
                                    key={
                                        student.student
                                    }
                                    variant="body2"
                                    fontWeight={600}
                                >
                                    •{" "}
                                    {
                                        student.student_full_name
                                    }
                                </Typography>
                            )
                        )}
                    </Stack>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        {unsavedChangesDialog.mode ===
                        "save"
                            ? "Select these students to review and include them, or save only the students already checked."
                            : "Go back and select these students to review and save their changes, or proceed and lose them."}
                    </Typography>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        pb: 2.5,
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Button
                        onClick={
                            handleCancelUnsavedDialog
                        }
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            color: "#586735",
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        onClick={
                            unsavedChangesDialog.mode ===
                            "save"
                                ? handleSaveSelectedOnly
                                : handleProceedAnyway
                        }
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            color: "#9A6A00",
                        }}
                    >
                        {unsavedChangesDialog.mode ===
                        "save"
                            ? "Save Selected Only"
                            : "Proceed Anyway"}
                    </Button>

                    <Button
                        variant="contained"
                        onClick={
                            handleSelectUnsavedStudents
                        }
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            backgroundColor: "#738B3B",

                            "&:hover": {
                                backgroundColor: "#647A33",
                            },
                        }}
                    >
                        Select These Students
                    </Button>
                </DialogActions>
            </Dialog>


            {/* =============================================
                SAVE VALIDATION POPUP
            ============================================= */}

            <Dialog
                open={
                    Boolean(saveError)
                }

                onClose={
                    clearSaveError
                }

                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontWeight: 700,
                        color: "#9A6A00",
                    }}
                >
                    <WarningIcon
                        sx={{
                            color: "#EFCE86",
                        }}
                    />
                    Can't Save Assessment
                </DialogTitle>

                <DialogContent>
                    <Typography variant="body2">
                        {saveError}
                    </Typography>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        pb: 2.5,
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={
                            clearSaveError
                        }

                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            backgroundColor: "#738B3B",

                            "&:hover": {
                                backgroundColor: "#647A33",
                            },
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>


            {/* =============================================
                SAVE OVERLAY
            ============================================= */}

            {isSaving && (
                <Box
                    sx={{
                        position: "fixed",
                        inset: 0,

                        backgroundColor:
                            "rgba(255,255,255,0.88)",

                        backdropFilter:
                            "blur(6px)",

                        zIndex: 2000,

                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            p: 4,
                            borderRadius: 4,
                            mx: 2,
                        }}
                    >
                        <FloatingGradelyLoader
                            size={60}
                            showText={true}
                            text="Saving assessments..."
                        />
                    </Paper>
                </Box>
            )}

        </Box>
    );
});

BatchUnitProgressCardGenerator.displayName =
    "BatchUnitProgressCardGenerator";


export default BatchUnitProgressCardGenerator;